import type { AnalyticsAdapter, AnalyticsContext, AnalyticsEvent, ConsentLevel } from './types';
import { detectContext } from './context';

const CONSENT_HIERARCHY: Record<ConsentLevel, number> = {
  essential: 0,
  analytics: 1,
  marketing: 2,
};

function meetsConsent(required: ConsentLevel, current: ConsentLevel): boolean {
  return CONSENT_HIERARCHY[current] >= CONSENT_HIERARCHY[required];
}

class AnalyticsRegistry {
  private adapters: AnalyticsAdapter[] = [];
  private ctx: AnalyticsContext | null = null;
  private initialized = false;

  init() {
    if (this.initialized) return;
    this.ctx = detectContext();
    this.initialized = true;
  }

  register(adapter: AnalyticsAdapter) {
    if (this.adapters.some((a) => a.name === adapter.name)) return;
    this.adapters.push(adapter);
  }

  track(event: Omit<AnalyticsEvent, 'context'>, consent: ConsentLevel) {
    if (!this.ctx) this.init();
    const full: AnalyticsEvent = { ...event, context: this.ctx! };
    for (const a of this.adapters) {
      if (meetsConsent(a.requiredConsent, consent)) {
        try { a.track(full); } catch { /* adapter must not break the app */ }
      }
    }
  }

  page(path: string, consent: ConsentLevel, properties?: Record<string, unknown>) {
    for (const a of this.adapters) {
      if (a.page && meetsConsent(a.requiredConsent, consent)) {
        try { a.page(path, properties); } catch { /* */ }
      }
    }
  }

  identify(userId: string, consent: ConsentLevel, traits?: Record<string, unknown>) {
    for (const a of this.adapters) {
      if (a.identify && meetsConsent(a.requiredConsent, consent)) {
        try { a.identify(userId, traits); } catch { /* */ }
      }
    }
  }

  reset(consent: ConsentLevel) {
    for (const a of this.adapters) {
      if (a.reset && meetsConsent(a.requiredConsent, consent)) {
        try { a.reset(); } catch { /* */ }
      }
    }
  }
}

export const analytics = new AnalyticsRegistry();
